import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Box,
  FormControl,
  MenuItem,
  Paper,
  Select,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableFooter,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';
import moment from 'moment';
import { useParams } from 'react-router';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useFilterValues } from 'hooks/useFilterContext';
import { Header, tableContainerStyle } from 'components/tables/common';
import { getCompleteCellClass, getDataCellClass } from 'components/tables/common/simpleTableClasses';
import { formatCurrency } from 'utils';
import {
  createReconciliationDisplay,
} from '../../utils';
import TableLoading from 'components/tables/common/TableLoading';
import { useStoreScratcherReconciliationAudits } from '../../hooks/queries/useStoreScratcherReconciliationAudits';
import { CheckMarkDisplay } from '../common';
import InventoryTicketMetric from './InventoryTicketMetric';
import toast from 'react-hot-toast';
import { buildCurrentInventoryStatusPath } from 'features/audits/utils';
import { padLeftNumeric, parseBarcodeFormat } from 'utils/barcodeFormat';

interface ScratcherAuditTableProps {
  onAuditDetails: (data: any) => void;
  selectedStore?: any;
}

const getNumber = (value: unknown): number => {
  const parsed = Number(value ?? 0);
  return Number.isFinite(parsed) ? parsed : 0;
};

const COLUMN_COUNT = 9;
const EXPECTED_SALES_COLUMN_INDEX = 5;
const POS_SALES_COLUMN_INDEX = 6;
const VARIANCE_COLUMN_INDEX = 7;
const RECONCILE_COLUMN_INDEX = 8;
const BORDER_COLUMNS = [0, VARIANCE_COLUMN_INDEX, RECONCILE_COLUMN_INDEX];
const TICKET_COLUMN_INDEXES = [1, 2, 3, 4];
const DOLLAR_COLUMN_INDEXES = [EXPECTED_SALES_COLUMN_INDEX, POS_SALES_COLUMN_INDEX, VARIANCE_COLUMN_INDEX];
const VARIANCE_COLUMN_STYLE: React.CSSProperties = { minWidth: '75px' };
const TICKET_COLUMN_STYLE: React.CSSProperties = VARIANCE_COLUMN_STYLE;
const DOLLAR_COLUMN_STYLE: React.CSSProperties = VARIANCE_COLUMN_STYLE;
const PHONE_DETECTION_BREAK = '\u200C';
const DATE_COLUMN_STYLE: React.CSSProperties = {
  width: '220px',
  minWidth: '220px',
  maxWidth: '220px',
  paddingLeft: '2px',
  paddingRight: '6px',
};
const SUBHEADER_LABELS = [
  'Opening Tickets',
  'Activated Tickets',
  'Moved to Backstock',
  'Closing Tickets',
  'Expected Sales',
  'POS Sales',
  'Variance',
];

const getMetricColumnStyle = (colIdx: number): React.CSSProperties | undefined => {
  if (colIdx === VARIANCE_COLUMN_INDEX) {
    return VARIANCE_COLUMN_STYLE;
  }

  if (TICKET_COLUMN_INDEXES.includes(colIdx)) {
    return TICKET_COLUMN_STYLE;
  }

  if (DOLLAR_COLUMN_INDEXES.includes(colIdx)) {
    return DOLLAR_COLUMN_STYLE;
  }

  return undefined;
};

const getMetricHeaderStyle = (colIdx: number): React.CSSProperties => ({
  ...getMetricColumnStyle(colIdx),
  textAlign: 'right',
});

const renderSubheaderText = (text: string, align: 'left' | 'right' = 'left') => {
  return (
    <Typography
      variant="caption"
      className={`table-subheader-text audit-table__horizontal-subheader-text audit-table__horizontal-subheader-text--${align}`}
      style={{ fontSize: '13px', fontWeight: 600 }}
    >
      {text}
    </Typography>
  );
};

const renderVarianceDisplay = (
  value: number,
  formatValue: (value: number) => string,
  title: string
) => {
  const variance = Number(value ?? 0);

  if (variance === 0) {
    return <CheckMarkDisplay value={0} title={`${title} matched`} />;
  }

  return (
    <span
      className={
        variance > 0
          ? 'audit-table__scratcher-variance audit-table__scratcher-variance--positive'
          : 'audit-table__scratcher-variance audit-table__scratcher-variance--negative'
      }
      title={title}
    >
      {formatValue(variance)}
    </span>
  );
};

const getTimestampMoment = (audit: any) => {
  const timestamp = audit?.report_datetime || audit?.audit_date || audit?.audit_created_at;

  if (!timestamp) {
    return null;
  }

  const parsed = moment.parseZone(timestamp);
  return parsed.isValid() ? parsed : null;
};

const formatShiftTime = (audit: any) => {
  const timestamp = getTimestampMoment(audit);

  if (!timestamp) {
    return 'Time unavailable';
  }

  return timestamp.format('h:mm A');
};

const formatShiftClockTime = (value: unknown) => {
  if (!value) {
    return '';
  }

  const parsed = moment(String(value), ['HH:mm:ss', 'HH:mm'], true);
  return parsed.isValid() ? parsed.format('h:mm A') : String(value);
};

const formatShiftRange = (audit: any) => {
  const startTime = formatShiftClockTime(audit?.shift_start_time) || formatShiftTime(audit);
  const endTime = formatShiftClockTime(audit?.shift_end_time);

  if (startTime && endTime) {
    return `${startTime} to ${endTime}`;
  }

  return startTime || 'Time unavailable';
};

const formatSourceShiftActivityTime = (activity: any) => {
  if (activity?.shift_date && activity?.shift_time) {
    const parsed = moment(`${activity.shift_date}T${activity.shift_time}`);
    if (parsed.isValid()) {
      return parsed.format('M/D h:mm A');
    }
  }

  const parsed = moment.parseZone(activity?.created_at);
  return parsed.isValid() ? parsed.format('M/D h:mm A') : 'Time unavailable';
};

const formatSourceShiftActivityLabel = (activity: any) => {
  if (activity?.event_type === 'pack_loaded') {
    return 'Pack loaded';
  }

  if (activity?.event_type === 'pack_removed') {
    return activity?.removed_type === 'sold_out' ? 'Pack sold out' : 'Pack removed';
  }

  return 'Pack activity';
};

const formatGamePackValue = (
  activity: any,
  barcodeFormat?: string | null
) => {
  if (!activity?.game_id || !activity?.pack_id) {
    return '';
  }

  try {
    const pattern = parseBarcodeFormat(barcodeFormat);
    const game = padLeftNumeric(activity.game_id, pattern.game.length).slice(-pattern.game.length);
    const pack = padLeftNumeric(activity.pack_id, pattern.pack.length).slice(-pattern.pack.length);
    return `${game}-${PHONE_DETECTION_BREAK}${pack}`;
  } catch (_error) {
    return `${activity.game_id}-${PHONE_DETECTION_BREAK}${activity.pack_id}`;
  }
};

const formatSourceShiftActivityOperator = (activity: any) => {
  return activity?.operator_name || activity?.created_by_name || '-';
};

const formatActivityDate = (value: unknown) => {
  if (!value) {
    return '';
  }

  const parsed = moment(String(value), 'YYYY-MM-DD', true);
  return parsed.isValid() ? parsed.format('M/D') : '';
};

const formatShiftEndDayOffset = (startValue: unknown, endValue: unknown) => {
  if (!startValue || !endValue) {
    return '';
  }

  const start = moment(String(startValue), 'YYYY-MM-DD', true);
  const end = moment(String(endValue), 'YYYY-MM-DD', true);
  if (!start.isValid() || !end.isValid()) {
    return '';
  }

  const dayDiff = end.startOf('day').diff(start.startOf('day'), 'days');
  if (dayDiff <= 0) {
    return '';
  }

  return ` (+${dayDiff} ${dayDiff === 1 ? 'day' : 'days'})`;
};

const formatAuditShiftDateRange = (audit: any) => {
  const rawStartDate = audit?.shift_start_date || audit?.report_date;
  const rawEndDate = audit?.shift_end_date || audit?.report_date;
  const startDate = formatActivityDate(rawStartDate);
  const endDate = formatActivityDate(rawEndDate);

  if (!startDate) {
    return formatShiftRange(audit);
  }

  const startTime = formatShiftClockTime(audit?.shift_start_time) || formatShiftTime(audit);
  const endTime = formatShiftClockTime(audit?.shift_end_time);

  if (!endTime) {
    return `${startDate} ${startTime}`;
  }

  if (endDate && endDate !== startDate) {
    return `${startDate} ${startTime} to ${endTime}${formatShiftEndDayOffset(rawStartDate, rawEndDate)}`;
  }

  return `${startDate} ${startTime} to ${endTime}`;
};

const formatLocationLabel = (audit: any) => {
  const key = String(audit?.location_key || '').trim();
  const name = String(audit?.location_name || '').trim();
  const type = String(audit?.location_type || '').trim();

  if (name) {
    return name;
  }
  if (key) {
    return key;
  }
  if (type) {
    return type;
  }
  return '';
};

const STORE_LOCATION_QUERY_KEY = 'storeLocation';

const getSourceKey = (audit: any) => {
  return String(audit?.store_location_id || audit?.location_key || '').trim();
};

const isCounterSourceOption = (option: { key: string; label: string; type: string }) => {
  const optionText = `${option.key} ${option.label} ${option.type}`.toLowerCase();
  return optionText.includes('counter');
};

const formatSignedTicketVariance = (value: number) => {
  if (value > 0) {
    return `+${value.toLocaleString()}`;
  }

  if (value < 0) {
    return `-${Math.abs(value).toLocaleString()}`;
  }

  return null;
};

const renderAmountWithQuantity = (
  amount: React.ReactNode,
  quantity: React.ReactNode,
  quantityLabel = 'tickets',
  className = ''
) => (
  <div className={`audit-table__amount-with-quantity ${className}`.trim()}>
    <span className="audit-table__amount-with-quantity-value">{amount}</span>
    {quantity != null && (
      <span className="audit-table__amount-with-quantity-subscript">
        {quantity} {quantityLabel}
      </span>
    )}
  </div>
);

type AuditDisplayGroup = {
  audit: any | null;
  activities: any[];
};

const ScratcherAuditTable: React.FC<ScratcherAuditTableProps> = ({ onAuditDetails, selectedStore }) => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const selectedStoreBarcodeFormat = selectedStore?.barcode_format;
  const tableShellRef = useRef<HTMLDivElement | null>(null);
  const [isPinnedToViewport, setIsPinnedToViewport] = useState(false);
  const [pinnedViewportHeight, setPinnedViewportHeight] = useState<number | null>(null);
  const { startDate, endDate } = useFilterValues();
  const [selectedSourceKey, setSelectedSourceKey] = React.useState('');
  const { data: audits = [], isLoading } = useStoreScratcherReconciliationAudits(
    id || '',
    startDate,
    endDate,
    true
  );
  const sortedAudits = useMemo(() => {
    return [...audits].sort((a, b) => {
      const aTime = new Date(a?.report_datetime || a?.audit_created_at || a?.report_date || 0).getTime();
      const bTime = new Date(b?.report_datetime || b?.audit_created_at || b?.report_date || 0).getTime();
      return bTime - aTime;
    });
  }, [audits]);
  const sourceOptions = useMemo(() => {
    const options = new Map<string, { key: string; label: string; type: string }>();
    audits.forEach((audit: any) => {
      const key = getSourceKey(audit);
      const label = formatLocationLabel(audit);
      const type = String(audit?.location_type || '').trim();
      if (!key || !label || options.has(key)) {
        return;
      }
      options.set(key, { key, label, type });
    });

    return Array.from(options.values())
      .sort((a, b) => a.label.localeCompare(b.label));
  }, [audits]);

  const defaultSourceKey = useMemo(() => {
    return sourceOptions.find(isCounterSourceOption)?.key || sourceOptions[0]?.key || '';
  }, [sourceOptions]);

  const effectiveSourceKey = selectedSourceKey || defaultSourceKey;

  const filteredAudits = useMemo(() => {
    if (!effectiveSourceKey) {
      return [];
    }

    return sortedAudits.filter((audit: any) => getSourceKey(audit) === effectiveSourceKey);
  }, [effectiveSourceKey, sortedAudits]);
  const groupedAudits = useMemo<AuditDisplayGroup[]>(() => {
    const groups: AuditDisplayGroup[] = [];
    let currentAuditGroup: AuditDisplayGroup | null = null;

    filteredAudits.forEach((audit: any) => {
      const backendActivities = Array.isArray(audit?.shift_activity) ? audit.shift_activity : [];

      if (audit?.is_shift_in_progress) {
        groups.push({ audit: null, activities: backendActivities });
        currentAuditGroup = null;
        return;
      }

      currentAuditGroup = { audit, activities: backendActivities };
      groups.push(currentAuditGroup);
    });

    return groups;
  }, [filteredAudits]);
  const shouldShowRowLocation = !effectiveSourceKey;
  const showSourceFilter = sourceOptions.length > 1;

  React.useEffect(() => {
    if (sourceOptions.length === 0) {
      return;
    }

    const sourceKeyFromUrl = searchParams.get(STORE_LOCATION_QUERY_KEY);
    const hasSourceKeyFromUrl = sourceKeyFromUrl
      ? sourceOptions.some((option) => option.key === sourceKeyFromUrl)
      : false;
    const nextSourceKey = hasSourceKeyFromUrl ? sourceKeyFromUrl : defaultSourceKey;

    if (nextSourceKey && selectedSourceKey !== nextSourceKey) {
      setSelectedSourceKey(nextSourceKey);
    }

    if (nextSourceKey && sourceKeyFromUrl !== nextSourceKey) {
      setSearchParams((currentParams) => {
        const nextParams = new URLSearchParams(currentParams);
        nextParams.set(STORE_LOCATION_QUERY_KEY, nextSourceKey);
        return nextParams;
      });
    }
  }, [defaultSourceKey, searchParams, selectedSourceKey, setSearchParams, sourceOptions]);

  const handleSourceChange = React.useCallback((nextSourceKey: string) => {
    setSelectedSourceKey(nextSourceKey);
    setSearchParams((currentParams) => {
      const nextParams = new URLSearchParams(currentParams);
      nextParams.set(STORE_LOCATION_QUERY_KEY, nextSourceKey);
      return nextParams;
    });
  }, [setSearchParams]);
  const hasTableRows = filteredAudits.length > 0;
  const canUsePinnedViewportMode = !isLoading && hasTableRows;
  const auditTableContainerClassName = [
    'audit-table__container',
    isLoading || !hasTableRows ? 'audit-table__container--compact' : '',
    canUsePinnedViewportMode && isPinnedToViewport ? 'audit-table__container--pinned' : '',
  ].filter(Boolean).join(' ');
  const auditTableShellClassName = [
    'audit-table__shell',
    canUsePinnedViewportMode && isPinnedToViewport ? 'audit-table__shell--pinned' : '',
  ].filter(Boolean).join(' ');

  const totals = useMemo(() => {
    const chronologicalAudits = [...filteredAudits].sort((a, b) => {
      const aTime = new Date(a?.report_datetime || a?.audit_created_at || a?.report_date || 0).getTime();
      const bTime = new Date(b?.report_datetime || b?.audit_created_at || b?.report_date || 0).getTime();
      return aTime - bTime;
    });
    const openingAuditSummary = chronologicalAudits[0]?.audit_summary || {};
    const closingAuditSummary = chronologicalAudits[chronologicalAudits.length - 1]?.audit_summary || {};

    const totalsRow = filteredAudits.reduce(
      (acc, audit) => {
        const auditSummary = audit?.audit_summary || {};
        const posSummary = audit?.pos_summary || {};
        const scratcherVariance = audit?.reconciliation_variance?.scratcher || {};

        acc.activatedCount += getNumber(auditSummary.newly_activated_count);
        acc.activatedPacks += getNumber(auditSummary.newly_activated_pack_count);
        acc.movedToBackstock += getNumber(auditSummary.moved_to_backstock_count);
        acc.movedToBackstockTickets += getNumber(
          auditSummary.moved_to_backstock_ticket_count ?? auditSummary.moved_to_backstock_count
        );
        acc.expectedSold += getNumber(auditSummary.total_sold);
        acc.expectedSales += getNumber(auditSummary.total_revenue ?? auditSummary.revenue);
        acc.posQty += getNumber(posSummary.scratcher_count ?? posSummary.scratcher_quantity_sold);
        acc.posSales += getNumber(posSummary.scratcher_sales);
        acc.qtyVariance += getNumber(scratcherVariance.quantity_variance);
        acc.revenueVariance += getNumber(scratcherVariance.revenue_variance);
        return acc;
      },
      {
        openingCount: 0,
        openingPacks: 0,
        activatedCount: 0,
        activatedPacks: 0,
        closingCount: 0,
        closingPacks: 0,
        movedToBackstock: 0,
        movedToBackstockTickets: 0,
        expectedSold: 0,
        expectedSales: 0,
        posQty: 0,
        posSales: 0,
        qtyVariance: 0,
        revenueVariance: 0,
      }
    );

    totalsRow.openingCount = getNumber(openingAuditSummary.opening_count);
    totalsRow.openingPacks = getNumber(openingAuditSummary.opening_pack_count);
    totalsRow.closingCount = getNumber(closingAuditSummary.closing_count);
    totalsRow.closingPacks = getNumber(closingAuditSummary.closing_pack_count);

    return totalsRow;
  }, [filteredAudits]);

  useEffect(() => {
    if (!canUsePinnedViewportMode) {
      setIsPinnedToViewport(false);
      setPinnedViewportHeight(null);
      return;
    }

    const getNavbarHeight = () => {
      const dashboardLayout = document.querySelector('.dashboard-layout');
      const cssValue = dashboardLayout
        ? window.getComputedStyle(dashboardLayout).getPropertyValue('--dashboard-appbar-height')
        : '';
      const parsedHeight = Number.parseFloat(cssValue);

      return Number.isFinite(parsedHeight) ? parsedHeight : 62;
    };

    const getPinnedOffset = () => {
      const shellElement = tableShellRef.current;
      const cssValue = shellElement
        ? window.getComputedStyle(shellElement).getPropertyValue('--audit-table-pinned-offset')
        : '';
      const parsedOffset = Number.parseFloat(cssValue);

      return Number.isFinite(parsedOffset) ? parsedOffset : 24;
    };

    const updatePinnedMetrics = () => {
      const shellElement = tableShellRef.current;

      if (!shellElement) {
        return;
      }

      const navbarHeight = getNavbarHeight();
      const pinnedOffset = getPinnedOffset();
      const pinStartTop = navbarHeight + pinnedOffset;
      const shellRect = shellElement.getBoundingClientRect();
      const shouldPinTable = shellRect.top <= pinStartTop;
      const nextPinnedHeight = Math.max(window.innerHeight - pinStartTop - 16, 320);

      setIsPinnedToViewport(shouldPinTable);
      setPinnedViewportHeight(nextPinnedHeight);
    };

    updatePinnedMetrics();
    let frameId = 0;

    const handleScroll = () => {
      if (frameId) {
        return;
      }

      frameId = window.requestAnimationFrame(() => {
        frameId = 0;
        updatePinnedMetrics();
      });
    };

    const handleResize = () => {
      updatePinnedMetrics();
    };

    window.addEventListener('scroll', handleScroll, { passive: true });
    window.addEventListener('resize', handleResize);

    return () => {
      if (frameId) {
        window.cancelAnimationFrame(frameId);
      }
      window.removeEventListener('scroll', handleScroll);
      window.removeEventListener('resize', handleResize);
    };
  }, [canUsePinnedViewportMode]);

  const handleAuditMovedToBackstockPacksClick = React.useCallback((
    event: React.MouseEvent<HTMLButtonElement>,
    audit: any
  ) => {
    event.stopPropagation();

    if (!id || !audit?.audit_id) {
      toast.error('No pack list is available for this count');
      return;
    }

    navigate(buildCurrentInventoryStatusPath(id, {
      metric: 'moved_to_backstock',
      audit_id: audit.audit_id,
      report_date: audit.report_date,
    }));
  }, [id, navigate]);

  const handleOverallMovedToBackstockPacksClick = React.useCallback((event: React.MouseEvent<HTMLButtonElement>) => {
    event.stopPropagation();

    if (!id || !startDate || !endDate) {
      return;
    }

    navigate(buildCurrentInventoryStatusPath(id, {
      metric: 'moved_to_backstock',
      start_date: startDate,
      end_date: endDate,
    }));
  }, [endDate, id, navigate, startDate]);

  const renderActivityList = React.useCallback((activities: any[], title = 'Shift activity') => {
    if (activities.length === 0) {
      return null;
    }

    const activityRowHeight = 32 + activities.length * 17;

    return (
      <TableRow className={[
        'audit-table__activity-list-row',
        'audit-table__audit-block-row--end',
      ].filter(Boolean).join(' ')}
        style={{ ['--audit-activity-row-height' as string]: `${activityRowHeight}px` }}
      >
        {Array.from({ length: COLUMN_COUNT }).map((_, colIdx) => (
          <TableCell
            key={`activity-cell-${colIdx}`}
            className={[
              getDataCellClass(colIdx, 0, 2, BORDER_COLUMNS, COLUMN_COUNT),
              colIdx === 0 ? 'audit-table__activity-list-cell' : 'audit-table__activity-list-empty-cell',
              colIdx === VARIANCE_COLUMN_INDEX ? 'audit-table__variance-dollar-right-border' : '',
            ].filter(Boolean).join(' ')}
            style={colIdx === 0 ? DATE_COLUMN_STYLE : getMetricColumnStyle(colIdx)}
          >
            {colIdx === 0 ? (
              <div className="audit-table__activity-list">
                <div className="audit-table__activity-list-heading">{title}</div>
                {activities.map((activity) => {
                  const gamePack = formatGamePackValue(activity, activity?.barcode_format || selectedStoreBarcodeFormat);
                  const activityParts = [
                    formatSourceShiftActivityTime(activity),
                    formatSourceShiftActivityLabel(activity),
                    (
                      <>
                        {gamePack ? (
                          <>
                            Game{' '}
                            <span className="audit-table__activity-game-pack">
                              {gamePack}
                            </span>
                          </>
                        ) : null}
                        {activity?.slot_id ? ` - Bin ${activity.slot_id}` : ''}
                      </>
                    ),
                    `By ${formatSourceShiftActivityOperator(activity)}`,
                  ];
                  const activityKey = activity?.event_id || activity?.activity_id || `${activity?.created_at || activity?.report_datetime}-${activity?.event_type}-${activity?.game_id}-${activity?.pack_id}`;

                  return (
                    <div
                      key={activityKey}
                      className={[
                        'audit-table__activity-list-item',
                        'audit-table__activity-list-item--source',
                      ].filter(Boolean).join(' ')}
                    >
                      {activityParts.map((part, index) => (
                        <span
                          key={`${activityKey}-${index}`}
                          className={[
                            'audit-table__activity-list-column',
                            index === 1 ? 'audit-table__activity-list-action' : '',
                            index === 3 ? 'audit-table__activity-list-sales' : '',
                          ].filter(Boolean).join(' ')}
                        >
                          {part}
                        </span>
                      ))}
                    </div>
                  );
                })}
              </div>
            ) : null}
          </TableCell>
        ))}
      </TableRow>
    );
  }, [selectedStoreBarcodeFormat]);

  return (
    <>
      {showSourceFilter && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end', mb: 1.5 }}>
          <FormControl size="small" sx={{ minWidth: 220 }}>
            <Select
              value={effectiveSourceKey}
              onChange={(event) => handleSourceChange(String(event.target.value))}
              inputProps={{ 'aria-label': 'Store location' }}
            >
              {sourceOptions.map((option) => (
                <MenuItem key={option.key} value={option.key}>
                  {option.label}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
        </Box>
      )}

      <div
        ref={tableShellRef}
        className={auditTableShellClassName}
        style={
          canUsePinnedViewportMode && pinnedViewportHeight
            ? { ['--audit-table-pinned-height' as string]: `${pinnedViewportHeight}px` }
            : undefined
        }
      >
        <TableContainer component={Paper} sx={tableContainerStyle} className={auditTableContainerClassName}>
          <Table size="small" stickyHeader>
            <TableHead>
            <TableRow>
              <TableCell
                rowSpan={2}
                className={getCompleteCellClass({ rowIdx: 0, colIdx: 0, totalRows: 1, totalCols: COLUMN_COUNT })}
                style={DATE_COLUMN_STYLE}
              >
                <p className="table-header-text audit-table__header-text--left">Date & Time</p>
                <p className="table-subheader-text audit-table__header-text--left">
                  Shift timestamp / Auditor
                </p>
              </TableCell>
              <Header
                title="Scratcher"
                cellStyle={{
                  className: `${getCompleteCellClass({ rowIdx: 0, colIdx: 1, totalRows: 2, totalCols: COLUMN_COUNT })} audit-table__variance-dollar-right-border`
                }}
                colSpan={7}
              />
              <TableCell
                rowSpan={2}
                className={getCompleteCellClass({ rowIdx: 0, colIdx: RECONCILE_COLUMN_INDEX, totalRows: 1, totalCols: COLUMN_COUNT })}
                style={VARIANCE_COLUMN_STYLE}
              >
                <p className="table-header-text audit-table__header-text--left">Reconcile</p>
                <p className="table-subheader-text audit-table__header-text--left">
                  Discrepancy / Reconciliation
                </p>
              </TableCell>
            </TableRow>
            <TableRow>
              {SUBHEADER_LABELS.map((label, index) => {
                const colIdx = index + 1;
                return (
                  <TableCell
                    key={`${label}-${colIdx}`}
                    className={[
                      getCompleteCellClass({
                        rowIdx: 'subheader',
                        colIdx,
                        totalRows: 2,
                        totalCols: COLUMN_COUNT,
                        borderColumns: BORDER_COLUMNS,
                      }),
                      label === 'Variance' ? 'audit-table__variance-dollar-right-border' : '',
                    ].filter(Boolean).join(' ')}
                    style={getMetricHeaderStyle(colIdx)}
                  >
                    {renderSubheaderText(label, 'right')}
                  </TableCell>
                );
              })}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading ? (
              <TableLoading colSpan={COLUMN_COUNT} />
            ) : filteredAudits.length === 0 ? (
              <TableRow>
                <TableCell colSpan={COLUMN_COUNT} align="center">
                  No scratcher audits found
                </TableCell>
              </TableRow>
            ) : (
              groupedAudits.map((group, rowIdx) => {
                const audit = group.audit;

                if (!audit) {
                  const firstActivity = group.activities[0];
                  return (
                    <React.Fragment key={`activity-${firstActivity?.audit_id || rowIdx}`}>
                      <TableRow
                        className={[
                          "audit-table__audit-block-row",
                          "audit-table__audit-block-row--start",
                          rowIdx % 2 === 0 ? "audit-table__row--even" : "audit-table__row--odd",
                        ].filter(Boolean).join(" ")}
                      >
                        <TableCell className={getDataCellClass(0, rowIdx, filteredAudits.length, BORDER_COLUMNS)} style={DATE_COLUMN_STYLE}>
                          <div className="audit-table__date-cell-content">
                            <div className="audit-table__date-text-block">
                              <div className="audit-table__audit-meta-grid">
                                <p className="audit-table__shift-meta">
                                  <span className="audit-table__shift-time">
                                    {formatActivityDate(firstActivity?.shift_start_date || firstActivity?.report_date)
                                      ? `${formatActivityDate(firstActivity?.shift_start_date || firstActivity?.report_date)} Shift in progress`
                                      : 'Shift in progress'}
                                  </span>
                                </p>
                                <span className="audit-table__date-auditor-name">
                                  {firstActivity?.user_name || firstActivity?.operator_name || '-'}
                                </span>
                              </div>
                            </div>
                          </div>
                        </TableCell>
                        {Array.from({ length: COLUMN_COUNT - 1 }).map((_, index) => {
                          const colIdx = index + 1;
                          return (
                            <TableCell
                              key={`shift-in-progress-${colIdx}`}
                              className={[
                                getDataCellClass(colIdx, rowIdx, filteredAudits.length, BORDER_COLUMNS),
                                colIdx === VARIANCE_COLUMN_INDEX ? 'audit-table__variance-dollar-right-border' : '',
                              ].filter(Boolean).join(' ')}
                              style={getMetricColumnStyle(colIdx)}
                            />
                          );
                        })}
                      </TableRow>
                      {renderActivityList(group.activities)}
                    </React.Fragment>
                  );
                }

                const auditSummary = audit?.audit_summary || {};
                const posSummary = audit?.pos_summary || {};
                const scratcherVariance = audit?.reconciliation_variance?.scratcher || {};
                const qtyVariance = getNumber(scratcherVariance.quantity_variance);
                const revenueVariance = getNumber(scratcherVariance.revenue_variance);
                const posScratcherQty = getNumber(posSummary.scratcher_count ?? posSummary.scratcher_quantity_sold);
                const expectedTicketsSold = getNumber(auditSummary.total_sold);
                const posScratcherSales = getNumber(posSummary.scratcher_sales);
                const expectedScratcherSales = getNumber(auditSummary.total_revenue ?? auditSummary.revenue);
                const handleReconcile = (event: React.MouseEvent) => {
                  event.stopPropagation();
                  onAuditDetails(audit);
                };

                return (
                  <React.Fragment key={audit?.audit_id || `${audit?.report_date}-${rowIdx}`}>
                <TableRow
                  hover
                  className={[
                    "audit-table__audit-block-row",
                    group.activities.length > 0 ? "audit-table__audit-block-row--start" : "audit-table__audit-block-row--single",
                    rowIdx % 2 === 0 ? "audit-table__row--even" : "audit-table__row--odd",
                    "cursor-pointer",
                  ].filter(Boolean).join(" ")}
                  onClick={() => onAuditDetails(audit)}
                >
                  <TableCell className={getDataCellClass(0, rowIdx, filteredAudits.length, BORDER_COLUMNS)} style={DATE_COLUMN_STYLE}>
                    <div className="audit-table__date-cell-content">
                      <div className="audit-table__date-text-block">
                        <div className="audit-table__audit-meta-grid">
                          <p className="audit-table__shift-meta">
                            <span className="audit-table__shift-time">{formatAuditShiftDateRange(audit)}</span>
                            {audit?.is_autogenerated && (
                              <span className="audit-table__auto-shift-badge">Auto audit</span>
                            )}
                          </p>
                          <span className="audit-table__date-auditor-name">{audit?.user_name || audit?.operator_name || '-'}</span>
                          {shouldShowRowLocation && formatLocationLabel(audit) ? (
                            <span className="audit-table__location-badge">{formatLocationLabel(audit)}</span>
                          ) : (
                            <span />
                          )}
                        </div>
                      </div>
                    </div>
                  </TableCell>
                  <TableCell align="right" className={getDataCellClass(1, rowIdx, filteredAudits.length, BORDER_COLUMNS)} style={getMetricColumnStyle(1)}>
                    <InventoryTicketMetric
                      tickets={auditSummary.opening_count}
                      packs={auditSummary.opening_pack_count}
                      emphasized={false}
                    />
                  </TableCell>
                  <TableCell align="right" className={getDataCellClass(2, rowIdx, filteredAudits.length, BORDER_COLUMNS)} style={getMetricColumnStyle(2)}>
                    <InventoryTicketMetric
                      tickets={auditSummary.newly_activated_count}
                      packs={auditSummary.newly_activated_pack_count}
                      emphasized={false}
                    />
                  </TableCell>
                  <TableCell align="right" className={getDataCellClass(3, rowIdx, filteredAudits.length, BORDER_COLUMNS)} style={getMetricColumnStyle(3)}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                      <InventoryTicketMetric
                        tickets={auditSummary.moved_to_backstock_ticket_count ?? auditSummary.moved_to_backstock_count}
                        packs={auditSummary.moved_to_backstock_count}
                        emphasized={false}
                        onPackClick={(event) => handleAuditMovedToBackstockPacksClick(event, audit)}
                        packTitle="Open these moved-to-backstock packs in current inventory"
                      />
                    </Box>
                  </TableCell>
                  <TableCell align="right" className={getDataCellClass(4, rowIdx, filteredAudits.length, BORDER_COLUMNS)} style={getMetricColumnStyle(4)}>
                    <InventoryTicketMetric
                      tickets={auditSummary.closing_count}
                      packs={auditSummary.closing_pack_count}
                      emphasized={false}
                    />
                  </TableCell>
                  <TableCell align="right" className={getDataCellClass(EXPECTED_SALES_COLUMN_INDEX, rowIdx, filteredAudits.length, BORDER_COLUMNS)} style={getMetricColumnStyle(EXPECTED_SALES_COLUMN_INDEX)}>
                    {renderAmountWithQuantity(formatCurrency(expectedScratcherSales), expectedTicketsSold.toLocaleString())}
                  </TableCell>
                  <TableCell align="right" className={getDataCellClass(POS_SALES_COLUMN_INDEX, rowIdx, filteredAudits.length, BORDER_COLUMNS)} style={getMetricColumnStyle(POS_SALES_COLUMN_INDEX)}>
                    {renderAmountWithQuantity(formatCurrency(posScratcherSales), posScratcherQty.toLocaleString())}
                  </TableCell>
                  <TableCell
                    align="right"
                    className={`${getDataCellClass(VARIANCE_COLUMN_INDEX, rowIdx, filteredAudits.length, BORDER_COLUMNS)} audit-table__variance-dollar-right-border`}
                    style={getMetricColumnStyle(VARIANCE_COLUMN_INDEX)}
                  >
                    {renderAmountWithQuantity(
                      renderVarianceDisplay(revenueVariance, formatCurrency, 'Scratcher dollar variance'),
                      formatSignedTicketVariance(qtyVariance),
                      'tickets',
                      revenueVariance > 0
                        ? 'audit-table__amount-with-quantity--positive'
                        : revenueVariance < 0
                          ? 'audit-table__amount-with-quantity--negative'
                          : 'audit-table__amount-with-quantity--center'
                    )}
                  </TableCell>
                  <TableCell className={getDataCellClass(RECONCILE_COLUMN_INDEX, rowIdx, filteredAudits.length, BORDER_COLUMNS)}>
                    {createReconciliationDisplay(
                      revenueVariance,
                      audit?.reconciliation,
                      handleReconcile,
                      true,
                      true,
                      audit?.verified
                    )}
                  </TableCell>
                </TableRow>
                    {renderActivityList(group.activities)}
                  </React.Fragment>
                );
              })
            )}
          </TableBody>
          {filteredAudits.length > 0 && (
            <TableFooter className="audit-table__footer">
              <TableRow className="audit-table__total-row">
                <TableCell className={`${getDataCellClass(0, 0, 1, BORDER_COLUMNS)} audit-table__total-label-cell`} style={DATE_COLUMN_STYLE}>
                  <span className="audit-table__total-label">Total</span>
                </TableCell>
                <TableCell align="right" className={getDataCellClass(1, 0, 1, BORDER_COLUMNS)} style={getMetricColumnStyle(1)}>
                  <InventoryTicketMetric tickets={totals.openingCount} packs={totals.openingPacks} emphasized={false} />
                </TableCell>
                <TableCell align="right" className={getDataCellClass(2, 0, 1, BORDER_COLUMNS)} style={getMetricColumnStyle(2)}>
                  <InventoryTicketMetric tickets={totals.activatedCount} packs={totals.activatedPacks} emphasized={false} />
                </TableCell>
                <TableCell align="right" className={getDataCellClass(3, 0, 1, BORDER_COLUMNS)} style={getMetricColumnStyle(3)}>
                  <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'flex-end', gap: 0.5 }}>
                    <InventoryTicketMetric
                      tickets={totals.movedToBackstockTickets}
                      packs={totals.movedToBackstock}
                      emphasized={false}
                      onPackClick={handleOverallMovedToBackstockPacksClick}
                      packTitle="Open these moved-to-backstock packs in current inventory"
                    />
                  </Box>
                </TableCell>
                <TableCell align="right" className={getDataCellClass(4, 0, 1, BORDER_COLUMNS)} style={getMetricColumnStyle(4)}>
                  <InventoryTicketMetric tickets={totals.closingCount} packs={totals.closingPacks} emphasized={false} />
                </TableCell>
                <TableCell align="right" className={getDataCellClass(EXPECTED_SALES_COLUMN_INDEX, 0, 1, BORDER_COLUMNS)} style={getMetricColumnStyle(EXPECTED_SALES_COLUMN_INDEX)}>
                  {renderAmountWithQuantity(formatCurrency(totals.expectedSales), totals.expectedSold.toLocaleString())}
                </TableCell>
                <TableCell align="right" className={getDataCellClass(POS_SALES_COLUMN_INDEX, 0, 1, BORDER_COLUMNS)} style={getMetricColumnStyle(POS_SALES_COLUMN_INDEX)}>
                  {renderAmountWithQuantity(formatCurrency(totals.posSales), totals.posQty.toLocaleString())}
                </TableCell>
                <TableCell
                  align="right"
                  className={`${getDataCellClass(VARIANCE_COLUMN_INDEX, 0, 1, BORDER_COLUMNS)} audit-table__variance-dollar-right-border`}
                  style={getMetricColumnStyle(VARIANCE_COLUMN_INDEX)}
                >
                  {renderAmountWithQuantity(
                    renderVarianceDisplay(totals.revenueVariance, formatCurrency, 'Scratcher dollar variance'),
                    formatSignedTicketVariance(totals.qtyVariance),
                    'tickets',
                    totals.revenueVariance > 0
                      ? 'audit-table__amount-with-quantity--positive'
                      : totals.revenueVariance < 0
                        ? 'audit-table__amount-with-quantity--negative'
                        : 'audit-table__amount-with-quantity--center'
                  )}
                </TableCell>
                <TableCell className={getDataCellClass(RECONCILE_COLUMN_INDEX, 0, 1, BORDER_COLUMNS)} />
              </TableRow>
            </TableFooter>
          )}
        </Table>
      </TableContainer>
      </div>
    </>
  );
};

export default ScratcherAuditTable;
